import { FileText, X } from "lucide-react";
import { useEffect } from "react";

import { useGetJobById } from "@elation/api-client/src/stitch-web";

import { Text } from "@src/shared/components/general/Text";
import {
  HorizontalFlexbox,
  VerticalFlexbox,
} from "@src/shared/components/layout/Flexbox";
import { Progress } from "@src/shared/components/ui/progress";
import { cn } from "@src/shared/lib/utils";

import type { DocumentJob } from "./useDocumentJobs";

interface JobMetadata {
  step?: "extracting" | "persisting";
  extractedCount?: number;
  opportunitiesCreated?: number;
}

function deriveProgress(
  status: string | undefined,
  isFailed: boolean,
  metadata?: JobMetadata,
) {
  if (isFailed) return { value: 100, subtitle: "Processing failed" };
  if (status === "completed") {
    const n = metadata?.opportunitiesCreated;
    return {
      value: 100,
      subtitle: n != null ? `Complete — ${n} opportunities found` : "Complete",
    };
  }
  if (status === "running") {
    if (metadata?.step === "persisting") {
      const n = metadata?.extractedCount;
      return {
        value: 75,
        subtitle: n != null ? `Saving ${n} findings...` : "Saving findings...",
      };
    }
    return { value: 40, subtitle: "Extracting opportunities..." };
  }
  return { value: 15, subtitle: "Uploading..." };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const REMOVE_DELAY_MS = 3000;

interface JobCardProps {
  job: DocumentJob;
  onComplete: (jobId: string) => void;
  onRemove: (jobId: string) => void;
}

export const JobCard = ({ job, onComplete, onRemove }: JobCardProps) => {
  const { data, isError } = useGetJobById(job.jobId, {
    query: {
      enabled: !job.failed,
      refetchInterval: (query) => {
        const status = query?.state?.data?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return 2000;
      },
      refetchOnMount: (query) => {
        const status = query.state.data?.data?.status;
        if (status === "completed" || status === "failed") return false;
        return true;
      },
    },
  });

  const status = data?.data?.status;
  const isFailed = job.failed || isError || status === "failed";
  const isCompleted = status === "completed";
  const { value, subtitle } = deriveProgress(
    status,
    isFailed,
    data?.data?.metadata as JobMetadata | undefined,
  );

  useEffect(() => {
    if (!isCompleted) return;
    onComplete(job.jobId);
    const removeTimer = setTimeout(() => onRemove(job.jobId), REMOVE_DELAY_MS);
    return () => clearTimeout(removeTimer);
  }, [isCompleted, job.jobId, onComplete, onRemove]);

  useEffect(() => {
    if (!isFailed) return;
    const removeTimer = setTimeout(() => onRemove(job.jobId), REMOVE_DELAY_MS);
    return () => clearTimeout(removeTimer);
  }, [isFailed, job.jobId, onRemove]);

  return (
    <HorizontalFlexbox
      alignItems="flex-start"
      gap="sm"
      className={cn(
        "rounded-lg border border-border bg-card p-3",
        isCompleted && "border-success/40 bg-success/5",
        isFailed && "border-destructive/40 bg-destructive/5",
      )}
    >
      <FileText className="mt-0.5 size-5 shrink-0 text-accent" />
      <VerticalFlexbox className="min-w-0 flex-1">
        <HorizontalFlexbox
          alignItems="flex-start"
          justifyContent="space-between"
          gap="xs"
        >
          <Text sizing="sm" fontWeight={500} className="line-clamp-1">
            {job.filename}
          </Text>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => onRemove(job.jobId)}
            className="shrink-0 rounded p-0.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </HorizontalFlexbox>
        <Progress
          value={value}
          className={cn(
            "mt-2",
            isFailed && "[&>div]:bg-destructive",
            isCompleted && "[&>div]:bg-success",
          )}
        />
        <Text
          sizing="xs"
          appearance="subdued"
          className="mt-1"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {job.fileSize != null ? `${formatBytes(job.fileSize)} · ` : ""}
          {subtitle}
        </Text>
      </VerticalFlexbox>
    </HorizontalFlexbox>
  );
};
